/**
 * LeadTable — sortable list of leads with inline notes editing, manual
 * re-analysis, and delete. Sort/order are controlled from the parent.
 */
import React, { useState } from 'react';
import { LeadsAPI, errorMessage } from '../services/api';

function Skeleton({ width = 'w-12' }) {
    return <div className={`h-4 ${width} mx-auto bg-gray-200 rounded animate-pulse`} />;
}

function ScorePill({ value, palette }) {
    if (value == null) return <span className="text-gray-400">—</span>;
    const p = palette[Math.min(Math.max(value, 1), 10)] || palette[5];
    return (
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${p}`}>
            {value} / 10
        </span>
    );
}

const SALES_PALETTE = {
    1: 'bg-gray-100 text-gray-600',  2: 'bg-gray-100 text-gray-600',
    3: 'bg-amber-50 text-amber-700', 4: 'bg-amber-50 text-amber-700',
    5: 'bg-amber-100 text-amber-800',6: 'bg-lime-100 text-lime-800',
    7: 'bg-green-100 text-green-800',8: 'bg-green-100 text-green-800',
    9: 'bg-emerald-200 text-emerald-900', 10: 'bg-emerald-200 text-emerald-900',
};

const URGENCY_PALETTE = {
    1: 'bg-gray-100 text-gray-600',  2: 'bg-gray-100 text-gray-600',
    3: 'bg-yellow-50 text-yellow-700', 4: 'bg-yellow-50 text-yellow-700',
    5: 'bg-yellow-100 text-yellow-800', 6: 'bg-orange-100 text-orange-800',
    7: 'bg-orange-100 text-orange-800', 8: 'bg-red-100 text-red-700',
    9: 'bg-red-200 text-red-800',     10: 'bg-red-300 text-red-900',
};

const OUTCOME_STYLES = {
    pending:     'bg-gray-100 text-gray-600',
    won:         'bg-green-100 text-green-700',
    lost:        'bg-red-100 text-red-700',
    no_response: 'bg-slate-100 text-slate-600',
};
const OUTCOME_LABELS = {
    pending:     'Pending',
    won:         'Won',
    lost:        'Lost',
    no_response: 'No response',
};
const OUTCOME_KEYS = ['pending', 'won', 'lost', 'no_response'];

function OutcomePill({ lead, onSelect, busy }) {
    const [open, setOpen] = React.useState(false);
    const current = lead.outcome || 'pending';
    return (
        <div className="relative inline-block">
            <button
                disabled={busy}
                onClick={() => setOpen((o) => !o)}
                className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${OUTCOME_STYLES[current]} disabled:opacity-50 cursor-pointer`}
            >
                {OUTCOME_LABELS[current]}
            </button>
            {open && !busy && (
                <div className="absolute z-20 left-0 top-7 bg-white border border-gray-200 rounded shadow-lg min-w-[9rem]">
                    {OUTCOME_KEYS.map((o) => (
                        <button
                            key={o}
                            onClick={() => { setOpen(false); onSelect(o); }}
                            className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${o === current ? 'font-semibold text-gray-900' : 'text-gray-700'}`}
                        >
                            {OUTCOME_LABELS[o]}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function StatusBadge({ status, error }) {
    const map = {
        pending:    'bg-gray-100 text-gray-700',
        processing: 'bg-blue-100 text-blue-700 animate-pulse',
        done:       'bg-green-100 text-green-700',
        error:      'bg-red-100 text-red-700',
    };
    return (
        <span title={error || ''} className={`text-xs px-2 py-0.5 rounded ${map[status] || map.pending}`}>
            {status}
        </span>
    );
}

const DEFAULT_EMPTY = (
    <>No leads yet. Head over to <a href="/upload" className="text-brand-600 underline">Upload</a> to add some.</>
);

export default function LeadTable({ leads, sortBy, order, onSortChange, onChange, onFlash, emptyMessage }) {
    const [editingId, setEditingId] = useState(null);
    const [draft, setDraft] = useState({});
    const [busyId, setBusyId] = useState(null);
    const [errorById, setErrorById] = useState({});

    function setError(id, msg) { setErrorById((s) => ({ ...s, [id]: msg })); }

    async function handleSaveNotes(lead) {
        try {
            setBusyId(lead.id);
            const { lead: updated } = await LeadsAPI.update(lead.id, { notes: draft.notes ?? lead.notes ?? '' });
            setEditingId(null);
            setDraft({});
            onChange?.(updated);
            onFlash?.('Notes saved');
        } catch (e) {
            setError(lead.id, errorMessage(e));
        } finally {
            setBusyId(null);
        }
    }

    async function handleDelete(lead) {
        if (!window.confirm(`Delete lead from ${lead.contact_name || lead.company_name || 'unknown'}?`)) return;
        try {
            setBusyId(lead.id);
            await LeadsAPI.remove(lead.id);
            onChange?.({ id: lead.id, _deleted: true });
            onFlash?.('Lead deleted');
        } catch (e) {
            setError(lead.id, errorMessage(e));
        } finally {
            setBusyId(null);
        }
    }

    async function handleOutcome(lead, newOutcome) {
        try {
            setBusyId(lead.id);
            const { lead: updated } = await LeadsAPI.update(lead.id, { outcome: newOutcome });
            onChange?.(updated);
            onFlash?.('Outcome updated');
        } catch (e) {
            setError(lead.id, errorMessage(e));
        } finally {
            setBusyId(null);
        }
    }

    async function handleAnalyze(lead) {
        try {
            setBusyId(lead.id);
            setError(lead.id, null);
            const { lead: updated } = await LeadsAPI.analyze(lead.id);
            onChange?.(updated);
            onFlash?.(`Re-scored — new sales score: ${updated.sales_probability}/10`);
        } catch (e) {
            setError(lead.id, errorMessage(e));
        } finally {
            setBusyId(null);
        }
    }

    function header(label, key, align = 'left') {
        const active = sortBy === key;
        return (
            <th
                onClick={() => onSortChange?.(key)}
                className={`px-3 py-2 text-${align} text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer select-none`}
            >
                <span className={active ? 'text-gray-900' : ''}>{label}</span>
                {active && <span className="ml-1">{order === 'asc' ? '▲' : '▼'}</span>}
            </th>
        );
    }

    if (!leads.length) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
                {emptyMessage ?? DEFAULT_EMPTY}
            </div>
        );
    }

    return (
        <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        {header('Contact', 'contact_name')}
                        {header('Company', 'company_name')}
                        {header('Sales',   'sales_probability', 'center')}
                        {header('Urgency', 'urgency_score',     'center')}
                        {header('Conf.',   'confidence_level',  'center')}
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Recommendation</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                        {header('Outcome', 'outcome')}
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {leads.map((l) => {
                        const isEditing = editingId === l.id;
                        const isLoading = l.analysis_status === 'pending' || l.analysis_status === 'processing';
                        return (
                            <React.Fragment key={l.id}>
                                <tr className="hover:bg-gray-50">
                                    <td className="px-3 py-2">
                                        <div className="font-medium text-gray-900">{l.contact_name || '—'}</div>
                                        <div
                                            title={l.email_text}
                                            className="text-xs text-gray-500 truncate max-w-xs"
                                        >
                                            {(l.email_text || '').slice(0, 80)}{(l.email_text || '').length > 80 ? '…' : ''}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-sm text-gray-700">{l.company_name || '—'}</td>
                                    <td className="px-3 py-2 text-center">
                                        {isLoading
                                            ? <Skeleton width="w-12" />
                                            : <ScorePill value={l.sales_probability} palette={SALES_PALETTE} />}
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                        {isLoading
                                            ? <Skeleton width="w-12" />
                                            : <ScorePill value={l.urgency_score} palette={URGENCY_PALETTE} />}
                                    </td>
                                    <td className="px-3 py-2 text-center text-sm text-gray-600">
                                        {isLoading
                                            ? <Skeleton width="w-10" />
                                            : (l.confidence_level != null ? `${l.confidence_level}%` : '—')}
                                    </td>
                                    <td className="px-3 py-2 text-sm text-gray-700">
                                        {isLoading
                                            ? <Skeleton width="w-24" />
                                            : (l.recommendation || '—')}
                                    </td>
                                    <td className="px-3 py-2"><StatusBadge status={l.analysis_status} error={l.analysis_error} /></td>
                                    <td className="px-3 py-2">
                                        <OutcomePill
                                            lead={l}
                                            busy={busyId === l.id}
                                            onSelect={(o) => handleOutcome(l, o)}
                                        />
                                    </td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap">
                                        <button
                                            onClick={() => { setEditingId(isEditing ? null : l.id); setDraft({ notes: l.notes || '' }); }}
                                            className="text-xs text-gray-600 hover:text-gray-900 mr-2"
                                        >
                                            {isEditing ? 'Close' : 'Notes'}
                                        </button>
                                        <button
                                            disabled={busyId === l.id || l.analysis_status === 'processing'}
                                            onClick={() => handleAnalyze(l)}
                                            className="text-xs text-brand-600 hover:text-brand-700 mr-2 disabled:opacity-50"
                                        >
                                            Re-score
                                        </button>
                                        <button
                                            disabled={busyId === l.id}
                                            onClick={() => handleDelete(l)}
                                            className="text-xs text-red-600 hover:text-red-700 disabled:opacity-50"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>

                                {(isEditing || l.reasoning || l.key_signals?.length || errorById[l.id]) && (
                                    <tr>
                                        <td colSpan={9} className="px-3 pb-3 bg-gray-50">
                                            {l.reasoning && (
                                                <div className="text-xs text-gray-600 mt-2">
                                                    <span className="font-semibold">Why: </span>{l.reasoning}
                                                </div>
                                            )}
                                            {Array.isArray(l.key_signals) && l.key_signals.length > 0 && (
                                                <div className="text-xs text-gray-600 mt-1 flex gap-2 flex-wrap">
                                                    {l.key_signals.map((s, i) => (
                                                        <span key={i} className="px-2 py-0.5 rounded bg-white border border-gray-200">{s}</span>
                                                    ))}
                                                </div>
                                            )}
                                            {errorById[l.id] && (
                                                <div className="text-xs text-red-600 mt-2">{errorById[l.id]}</div>
                                            )}

                                            {isEditing && (
                                                <div className="mt-3">
                                                    <textarea
                                                        value={draft.notes ?? ''}
                                                        onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                                                        placeholder="Customer notes — only visible to you"
                                                        className="w-full text-sm border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
                                                        rows={3}
                                                    />
                                                    <div className="mt-2 flex gap-2">
                                                        <button
                                                            disabled={busyId === l.id}
                                                            onClick={() => handleSaveNotes(l)}
                                                            className="px-3 py-1.5 rounded bg-brand-500 text-white text-xs font-medium hover:bg-brand-600 disabled:opacity-50"
                                                        >
                                                            Save notes
                                                        </button>
                                                        <button
                                                            onClick={() => { setEditingId(null); setDraft({}); }}
                                                            className="px-3 py-1.5 rounded text-xs text-gray-600 hover:bg-gray-100"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
